import { Contract, EPageViewType, EPriceType } from '../../types';
import { IconButton } from '@portal_components/components/IconButton';
import { TableItem } from '@portal_components/components/TableItem';
import { UseFieldArrayRemove, useFormContext } from 'react-hook-form';
import { FormDropdown } from '@portal_components/components/FormDropdown';
import { FormTextInput } from '@portal_components/components/FormTextInput';
import { useContractStandingData } from '../../hooks/useContractStandingData';

interface ServiceSiteDetailsListItemProps {
  serviceIndex: number;
  siteDetailIndex: number;
  readonly: boolean;
  viewType?: EPageViewType;
  expandedPricingMatrix: string | null;
  setExpandedPricingMatrix: (expandedPricingMatrix: string | null) => void;
  removeSiteDetail: UseFieldArrayRemove;
}

export const ServiceSiteDetailsListItem = ({
  siteDetailIndex,
  serviceIndex,
  readonly,
  viewType,
  expandedPricingMatrix,
  setExpandedPricingMatrix,
  removeSiteDetail
}: ServiceSiteDetailsListItemProps) => {
  const { watch, setValue } = useFormContext<Contract>();
  const [markedForDeletion, newSiteDetail] = watch([
    `serviceList.${serviceIndex}.siteDetails.${siteDetailIndex}.markedForDeletion`,
    `serviceList.${serviceIndex}.siteDetails.${siteDetailIndex}.newSiteDetail`
  ]);
  const {
    standingData: { standingDataSiteTypeList }
  } = useContractStandingData();

  const handleRemove = () => {
    if (viewType === EPageViewType.EDIT && !newSiteDetail) {
      setValue(
        `serviceList.${serviceIndex}.siteDetails.${siteDetailIndex}.markedForDeletion`,
        !markedForDeletion
      );
      return;
    }
    removeSiteDetail(siteDetailIndex);
  };

  const StandardPricesTable = () => {
    return (
      <div className="pl-2">
        <div className="pb-1">
          <h2 className="text-2xl font-bold">{'Maintenance Prices (£ / annum)'}</h2>
        </div>
        <div className={`grid grid-cols-5 gap-2 items-start`}>
          <div key="GPRS">GPRS</div>
          <div key="GSM">GSM</div>
          <div key="PSTN">PSTN</div>
          <div key="PAKNET">PAKNET</div>
          <div key="Customer Owned">Customer Owned</div>
          {Object.values(EPriceType).map((priceType: EPriceType) => {
            return (
              <div key={`maintenance-${siteDetailIndex}${priceType}`} className="flex flex-col">
                <FormTextInput<Contract>
                  title="Non-inherited"
                  name={`serviceList.${serviceIndex}.siteDetails.${siteDetailIndex}.servicePriceList.${priceType}.nonInherited.annualBasePrice`}
                  readonly={readonly}
                />
                <FormTextInput<Contract>
                  title="Inherited"
                  name={`serviceList.${serviceIndex}.siteDetails.${siteDetailIndex}.servicePriceList.${priceType}.inherited.annualBasePrice`}
                  readonly={readonly}
                />
              </div>
            );
          })}
        </div>
        <div className="pt-2">
          <h2 className="text-2xl font-bold">{'Charging Prices (£ / annum)'}</h2>
        </div>
        <div className={`grid grid-cols-5 gap-2 items-start`}>
          <div key="GPRS">GPRS</div>
          <div key="GSM">GSM</div>
          <div key="PSTN">PSTN</div>
          <div key="PAKNET">PAKNET</div>
          <div key="Customer Owned">Customer Owned</div>
          {Object.values(EPriceType).map((priceType: EPriceType) => {
            return (
              <div key={`charging-${siteDetailIndex}${priceType}`} className="flex flex-col">
                <FormTextInput<Contract>
                  title="Non-inherited"
                  name={`serviceList.${serviceIndex}.siteDetails.${siteDetailIndex}.servicePriceList.${priceType}.nonInherited.chargingPrice`}
                  readonly={readonly}
                />
                <FormTextInput<Contract>
                  title="Inherited"
                  name={`serviceList.${serviceIndex}.siteDetails.${siteDetailIndex}.servicePriceList.${priceType}.inherited.chargingPrice`}
                  readonly={readonly}
                />
              </div>
            );
          })}
        </div>
        <div className="pt-2">
          <h2 className="text-2xl font-bold">{'Future Dated Maintenance Prices (£ / annum)'}</h2>
        </div>
        <div className={`grid grid-cols-5 gap-2 items-start`}>
          <div key="GPRS">GPRS</div>
          <div key="GSM">GSM</div>
          <div key="PSTN">PSTN</div>
          <div key="PAKNET">PAKNET</div>
          <div key="Customer Owned">Customer Owned</div>
          {Object.values(EPriceType).map((priceType: EPriceType) => {
            return (
              <div key={`futureDatedMaintenance-${siteDetailIndex}${priceType}`} className="flex flex-col">
                <FormTextInput<Contract>
                  title="Non-inherited"
                  name={`serviceList.${serviceIndex}.siteDetails.${siteDetailIndex}.futureDatedServicePriceList.${priceType}.nonInherited.annualBasePrice`}
                  readonly={readonly}
                />
                <FormTextInput<Contract>
                  title="Inherited"
                  name={`serviceList.${serviceIndex}.siteDetails.${siteDetailIndex}.futureDatedServicePriceList.${priceType}.inherited.annualBasePrice`}
                  readonly={readonly}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const handleViewPricingMatrixClicked = () => {
    if (expandedPricingMatrix === `${serviceIndex}-${siteDetailIndex}`) {
      setExpandedPricingMatrix(null);
      return;
    }
    setExpandedPricingMatrix(`${serviceIndex}-${siteDetailIndex}`);
  };

  return (
    <div>
      {markedForDeletion && <p className="text-feedback-orange text-sm">Site Details Marked for Deletion</p>}
      <TableItem>
        <FormDropdown<Contract>
          title="Site Type"
          name={`serviceList.${serviceIndex}.siteDetails.${siteDetailIndex}.siteTypeId`}
          placeholder="Select..."
          elementsForDisplay={standingDataSiteTypeList}
          readonly={readonly}
        />
        <div className="pt-3 w-full flex flex-row justify-between px-4">
          {!readonly && <IconButton icon="/delete.svg" onClick={handleRemove} />}
          <IconButton
            icon={
              expandedPricingMatrix === `${serviceIndex}-${siteDetailIndex}`
                ? '/arrowUp.svg'
                : '/arrowDown.svg'
            }
            onClick={handleViewPricingMatrixClicked}
          />
        </div>
      </TableItem>
      {expandedPricingMatrix === `${serviceIndex}-${siteDetailIndex}` && <StandardPricesTable />}
    </div>
  );
};
